/**
 * 冰箱切换器。
 *
 * 位置在顶部栏左侧 —— 这是 iOS 里"当前对象"的标准位置（左上角那个
 * 返回/切换按钮），用户不用找。刻意不做成页面里的一个下拉框：
 * 冰箱是这个应用最外层的上下文，它得一直在视野里，不能藏在二级页面。
 *
 * 面板里同时给出"新建""加入"两个动作。理由：想切换的人，
 * 十有八九是刚被拉进了一个新冰箱，或者想再开一个。放在同一处，
 * 省掉"先回设置页"那一步。
 */

import {
  HOUSEHOLD_NAME_MAX,
  formatInviteCode,
  isCompleteInviteCode,
  normalizeInviteCode,
} from '@fridge/shared';
import { useState } from 'react';

import {
  ApiError,
  createHousehold,
  joinHousehold,
  type HouseholdSession,
} from '../api/client';
import { deviceLabel } from '../lib/device';
import { GLYPHS, IconGlyph } from './IconGlyph';
import { Pressable } from './Pressable';
import { Sheet } from './Sheet';
import { useToast } from './Toast';
import { useAppData } from '../state/app-context';

type Mode = 'list' | 'create' | 'join';

export function HouseholdSwitcher(): JSX.Element | null {
  const { households, active, detail, switchHousehold, completeOnboarding, loading } =
    useAppData();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('list');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 服务端的名字比本地缓存的新（别人可能刚改过名）
  const currentName =
    detail && detail.household.id === active?.id ? detail.household.name : active?.name;

  if (!active) return null;

  function reset(): void {
    setMode('list');
    setName('');
    setCode('');
    setError(null);
  }

  async function handleSwitch(id: string): Promise<void> {
    if (id === active?.id) {
      setOpen(false);
      return;
    }
    setBusy(true);
    await switchHousehold(id);
    setBusy(false);
    setOpen(false);
    const target = households.find((h) => h.id === id);
    toast.show(`换到「${target?.name ?? '另一个冰箱'}」了。`, 'success');
  }

  async function handleSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const session: HouseholdSession =
        mode === 'create'
          ? await createHousehold(name.trim(), deviceLabel())
          : await joinHousehold(code, deviceLabel());
      await completeOnboarding(session);
      toast.show(
        mode === 'create'
          ? `「${session.household.name}」建好了。`
          : `进了「${session.household.name}」。`,
        'success',
      );
      setOpen(false);
      reset();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '没能连上服务器。');
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = mode === 'create' ? name.trim().length > 0 : isCompleteInviteCode(code);

  const title = mode === 'list' ? '切换冰箱' : mode === 'create' ? '新建一个冰箱' : '用邀请码加入';

  return (
    <>
      <Pressable
        aria-label="切换冰箱"
        scale={0.96}
        onClick={() => {
          reset();
          setOpen(true);
        }}
        /*
         * 顶部栏整体是 pointer-events-none 的（它只是层玻璃，不该挡住页面），
         * 所以这一颗必须自己把事件收回来。
         */
        className="pointer-events-auto flex h-8 max-w-[58vw] items-center gap-1.5 rounded-full bg-surface-2 pl-2.5 pr-2 text-label shadow-e1"
      >
        <IconGlyph glyph={GLYPHS.house} size={14} className="shrink-0 text-accent" weight="fill" />
        <span className="truncate text-[13px] font-medium">{currentName ?? '冰箱'}</span>
        <IconGlyph glyph={GLYPHS.down} size={11} className="shrink-0 text-label-2" weight="bold" />
      </Pressable>

      <Sheet
        open={open}
        onClose={() => {
          setOpen(false);
          reset();
        }}
        title={title}
        onExited={reset}
        footer={
          mode === 'list' ? undefined : (
            <div className="flex gap-2">
              <Pressable className="cta-quiet px-4" disabled={busy} onClick={reset}>
                返回
              </Pressable>
              <Pressable
                className="cta flex-1"
                disabled={!canSubmit || busy}
                onClick={() => void handleSubmit()}
              >
                {busy ? '稍等' : mode === 'create' ? '建好' : '进去'}
              </Pressable>
            </div>
          )
        }
      >
        {mode === 'list' ? (
          <div className="pb-2">
            <p className="mb-2 px-1 text-[12.5px] text-label-2">
              这台设备进过的冰箱。切换过去只看这一个冰箱的东西。
            </p>

            <ul className="group divide-y divide-separator">
              {households.map((household) => {
                const isActive = household.id === active.id;
                // 当前这个用刚拿到的详情，其它的用上次进去时缓存下来的数字
                const members =
                  isActive && detail
                    ? detail.household.memberCount
                    : household.memberCount;
                return (
                  <li key={household.id}>
                    <Pressable
                      scale={0.99}
                      disabled={busy}
                      onClick={() => void handleSwitch(household.id)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left"
                    >
                      <IconGlyph
                        glyph={GLYPHS.fridge}
                        size={20}
                        className={isActive ? 'text-accent' : 'text-label-2'}
                        weight={isActive ? 'fill' : 'regular'}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[15px] text-label">{household.name}</div>
                        <div className="mt-0.5 text-[11.5px] text-label-2">
                          {members ? `${members} 台设备` : '还没进去过'}
                          {isActive ? ' · 正在用' : ''}
                        </div>
                      </div>
                      {isActive ? (
                        <IconGlyph glyph={GLYPHS.check} size={16} className="text-accent" weight="bold" />
                      ) : null}
                    </Pressable>
                  </li>
                );
              })}
            </ul>

            <ul className="group mt-4 divide-y divide-separator">
              <li>
                <Pressable
                  scale={0.99}
                  disabled={busy || loading}
                  onClick={() => setMode('create')}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left"
                >
                  <IconGlyph glyph={GLYPHS.add} size={20} className="text-accent" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] text-label">新建一个冰箱</div>
                    <div className="mt-0.5 text-[11.5px] text-label-2">
                      一块全新的、跟现在这个互不相干的地方
                    </div>
                  </div>
                  <IconGlyph glyph={GLYPHS.next} size={14} className="text-label-3" />
                </Pressable>
              </li>
              <li>
                <Pressable
                  scale={0.99}
                  disabled={busy || loading}
                  onClick={() => setMode('join')}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left"
                >
                  <IconGlyph glyph={GLYPHS.invite} size={20} className="text-accent" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] text-label">用邀请码加入</div>
                    <div className="mt-0.5 text-[11.5px] text-label-2">
                      别人发来的 6 位数字
                    </div>
                  </div>
                  <IconGlyph glyph={GLYPHS.next} size={14} className="text-label-3" />
                </Pressable>
              </li>
            </ul>
          </div>
        ) : (
          <div className="flex flex-col gap-4 pb-2">
            {mode === 'create' ? (
              <div>
                <label className="field-label" htmlFor="switch-hh-name">
                  给它起个名字
                </label>
                <input
                  id="switch-hh-name"
                  className="field"
                  maxLength={HOUSEHOLD_NAME_MAX}
                  placeholder="比如 爸妈家的冰箱"
                  value={name}
                  autoFocus
                  onChange={(event) => setName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void handleSubmit();
                  }}
                />
                <p className="mt-2 text-[11.5px] leading-relaxed text-label-2">
                  建好之后它就是当前冰箱。原来那个还在，随时能从这个面板切回去。
                </p>
              </div>
            ) : (
              <div>
                <label className="field-label" htmlFor="switch-hh-code">
                  6 位邀请码
                </label>
                <input
                  id="switch-hh-code"
                  className="field tnum text-center text-[24px] tracking-[0.3em]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  autoFocus
                  value={formatInviteCode(code)}
                  onChange={(event) => setCode(normalizeInviteCode(event.target.value))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void handleSubmit();
                  }}
                />
                <p className="mt-2 text-[11.5px] leading-relaxed text-label-2">
                  加入之后，这台设备上就有两个冰箱了，左上角随时能换。
                </p>
              </div>
            )}

            {error ? <p className="text-[12.5px] leading-relaxed text-danger">{error}</p> : null}
          </div>
        )}
      </Sheet>
    </>
  );
}
